/**
 * @file 支付会话 hook
 * @description 封装完整支付流程：下单 → 二维码展示 → 轮询 → 成功/失败，对外暴露声明式 API
 */

import {useCallback, useEffect, useReducer, useRef} from 'react';
import {useMutation} from '@tanstack/react-query';
import type {UsePaymentSessionOptions, PaymentSession} from '../types';
import {paymentReducer} from '../paymentMachine';
import {useOrderPolling} from './useOrderPolling';
import {useQRCountdown} from './useQRCountdown';
import type {SkuInput} from '@/services/commerce/entities/payment';
import message from '@/components/common/Message';

export function usePaymentSession(opts: UsePaymentSessionOptions): PaymentSession {
    const [state, dispatch] = useReducer(paymentReducer, {status: 'idle'});

    // 通过 ref 持有最新值，避免 useCallback / useEffect 依赖数组膨胀
    const skuRef = useRef<SkuInput | null>(null);
    const optsRef = useRef(opts);
    optsRef.current = opts;

    // 用 useMutation 包裹业务侧 createOrder
    const createOrderMutation = useMutation({
        mutationFn: (sku: SkuInput) => optsRef.current.createOrder(sku),
        onSuccess: (order) => dispatch({type: 'ORDER_CREATED', order}),
        onError: (err: Error) => {
            dispatch({type: 'ORDER_CREATE_FAILED', reason: err?.message ?? '创建订单失败'});
            void message.error(err?.message || '创建订单失败，请稍后重试');
        },
    });

    // 副作用 1：进入 creating 态 → 触发下单 mutation
    useEffect(() => {
        if (state.status === 'creating' && skuRef.current) {
            createOrderMutation.mutate(skuRef.current);
        }
    }, [state.status]); // eslint-disable-line react-hooks/exhaustive-deps

    // 副作用 2：进入 qr-loading 态 → 简化实现视作立即加载完成
    // 真实场景可挂 antd QRCode 的 onLoad 事件回调 dispatch QR_LOADED
    useEffect(() => {
        if (state.status === 'qr-loading') {
            dispatch({type: 'QR_LOADED'});
        }
    }, [state.status]);

    // 副作用 3：进入 success 态 → 触发业务 onSuccess（带 rightsIssued 标记）
    useEffect(() => {
        if (state.status === 'success') {
            optsRef.current.onSuccess?.(state.result, state.rightsIssued);
        }
    }, [state.status]); // eslint-disable-line react-hooks/exhaustive-deps

    // 副作用 3b：权益下发通知（仅 PAID→RIGHTS_ISSUED 转换时触发一次）
    const prevStatusRef = useRef(state.status);
    const rightsIssued = state.status === 'success' ? state.rightsIssued : false;
    useEffect(() => {
        const wasAlreadySuccess = prevStatusRef.current === 'success';
        prevStatusRef.current = state.status;

        // 仅从 success(rightsIssued=false) 变为 success(rightsIssued=true) 时触发
        // 首次直达 success(rightsIssued=true) 时 wasAlreadySuccess 为 false，不触发
        if (state.status === 'success' && rightsIssued && wasAlreadySuccess) {
            optsRef.current.onRightsIssued?.(state.result);
        }
    }, [state.status, rightsIssued]); // eslint-disable-line react-hooks/exhaustive-deps

    // 副作用 4：订单轮询（react-query 内部处理竞态与 cleanup）
    useOrderPolling('order' in state ? state.order.orderId : null, (s) =>
        dispatch({type: 'POLL_TICK', status: s}),
    );

    // 副作用 5：二维码本地倒计时（到期触发 EXPIRED）
    useQRCountdown('order' in state ? state.order.qrCode.expireAt : null, () =>
        dispatch({type: 'EXPIRED'}),
    );

    // ─── 对外方法 ───

    const start = useCallback((sku: SkuInput) => {
        skuRef.current = sku;
        dispatch({type: 'START', sku});
    }, []);

    const switchSku = useCallback((sku: SkuInput) => {
        skuRef.current = sku;
        dispatch({type: 'SWITCH_SKU', sku});
    }, []);

    const refreshQR = useCallback(() => {
        dispatch({type: 'REFRESH_QR'});
    }, []);

    const cancel = useCallback(() => {
        dispatch({type: 'CANCEL'});
    }, []);

    // ─── 返回稳定引用对象（业务方无需 useRef 即可在 useCallback 里读到最新值） ───

    const sessionRef = useRef<PaymentSession>({} as PaymentSession);
    Object.assign(sessionRef.current, {
        state,
        start,
        switchSku,
        refreshQR,
        cancel,
        isSuccess: state.status === 'success',
        isPendingRights: state.status === 'success' && !state.rightsIssued,
    });

    return sessionRef.current;
}
